import { useCallback, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "bastion.starredChatSessionIds";

function parseStoredIds(raw: string | null): string[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    const seen = new Set<string>();
    const out: string[] = [];
    for (const x of parsed) {
      if (typeof x !== "string") continue;
      const id = x.trim();
      if (!id || seen.has(id)) continue;
      seen.add(id);
      out.push(id);
    }
    return out;
  } catch {
    return [];
  }
}

function loadStarredIds(): string[] {
  if (typeof window === "undefined") return [];
  return parseStoredIds(window.localStorage.getItem(STORAGE_KEY));
}

function persistStarredIds(ids: string[]) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
  } catch {
    // quota / private mode
  }
}

export function useStarredChats() {
  const [starredSessionIds, setStarredSessionIds] = useState<string[]>(loadStarredIds);

  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === STORAGE_KEY) {
        setStarredSessionIds(parseStoredIds(event.newValue));
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  const setAndPersist = useCallback((ids: string[]) => {
    setStarredSessionIds(ids);
    persistStarredIds(ids);
  }, []);

  const isStarred = useCallback(
    (sessionId: string) => starredSessionIds.includes(sessionId),
    [starredSessionIds],
  );

  const toggleStar = useCallback(
    (sessionId: string) => {
      const trimmed = sessionId.trim();
      if (!trimmed) return;
      const has = starredSessionIds.includes(trimmed);
      const next = has
        ? starredSessionIds.filter((id) => id !== trimmed)
        : [trimmed, ...starredSessionIds.filter((id) => id !== trimmed)];
      setAndPersist(next);
    },
    [starredSessionIds, setAndPersist],
  );

  const starredIdSet = useMemo(() => new Set(starredSessionIds), [starredSessionIds]);

  return {
    starredSessionIds,
    starredIdSet,
    isStarred,
    toggleStar,
  };
}
